import { forwardRef } from 'react';
import type { FC, KeyboardEvent, MouseEvent, Ref } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import {
  Button,
  defaultThemeProp,
  Tooltip,
  tryCatch,
  useConsolidatedRef,
  useElement,
  useI18n,
  calculateFontSize
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';

import { useRTEContext } from '../RichTextEditor.context';

import type { StyleType } from './Toolbar.types';

export interface ToolBarButtonProps {
  tooltip?: string;
  active?: boolean;
  styleType?: StyleType;
  disabled?: boolean;
  label?: string;
  onMouseDown?: (e: MouseEvent | KeyboardEvent) => void;
}

const StyledToolbarButton = styled(Button)<{ $active?: boolean; $styleType?: StyleType }>(props => {
  const { $active: active, theme, $styleType: styleType } = props;
  const size = `calc(3.75 * ${props.theme.base.spacing})`;
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  const activeColor = tryCatch(() =>
    mix(0.85, theme.base.palette['primary-background'], theme.base.palette.interactive)
  );
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    line-height: 1;
    align-items: center;
    justify-content: center;
    height: ${size};
    min-width: ${size};
    padding: 0;

    ${active &&
    css`
      background-color: ${activeColor};
      color: ${theme.base.palette.interactive};
      border-bottom: calc(0.25 * ${theme.base.spacing}) solid ${theme.base.palette.interactive};
    `}

    ${styleType === 'BOLD' &&
    css`
      font-weight: bold;
    `}

    ${styleType === 'ITALIC' &&
    css`
      font-style: italic;
    `}

    ${styleType === 'STRIKE-THROUGH' &&
    css`
      text-decoration: line-through;

      /* Override text-decoration: none hover style in StyledButton. */
      &&:hover {
        text-decoration: line-through;
      }
    `}

    ${styleType === 'UNDERLINE' &&
    css`
      text-decoration: underline;

      /* Override text-decoration: none hover style in StyledButton. */
      &&:hover {
        text-decoration: underline;
      }
    `}

    ${styleType === 'SUBSCRIPT' &&
    css`
      sub {
        font-size: ${fontSizes.xxs};
        vertical-align: sub;
        line-height: 1;
      }
    `}

    ${styleType === 'SUPERSCRIPT' &&
    css`
      sup {
        font-size: ${fontSizes.xxs};
        vertical-align: super;
        line-height: 1;
      }
    `}
  `;
});

StyledToolbarButton.defaultProps = defaultThemeProp;

const ToolbarButton: FC<ToolBarButtonProps & ForwardProps> = forwardRef(function ToolbarButton(
  { tooltip, active, styleType, disabled, label, onMouseDown, ...restProps },
  ref: Ref<HTMLButtonElement>
) {
  const { disabled: contextDisabled } = useRTEContext();
  const [el, setEl] = useElement();
  const toolBarRef = useConsolidatedRef<HTMLButtonElement>(setEl, ref);
  const t = useI18n();
  return (
    <>
      <StyledToolbarButton
        variant='simple'
        $active={active}
        $styleType={styleType}
        ref={toolBarRef}
        disabled={contextDisabled || disabled}
        data-toolbar-item='true'
        onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
          if (e.key === 'Enter') {
            onMouseDown?.(e);
          }
        }}
        onMouseDown={onMouseDown}
        type='button'
        aria-label={`${label}${active ? ` - ${t('active')}` : ''}`}
        aria-pressed={active}
        {...restProps}
      />
      {el && tooltip && (
        <Tooltip target={el} showDelay='none' hideDelay='none'>
          {tooltip}
        </Tooltip>
      )}
    </>
  );
});

export default ToolbarButton;
